import { useEffect, useState } from 'react';
import { useTopTabNav } from '@vivli/core/infrastructure/hook';
import { IPageLink } from '@vivli/shared/infrastructure/interface';
import { useLocation, useNavigate } from 'react-router';
import { HorizontalNavView } from '@vivli/shared/views';

interface HorizontalNavContainerComponentProps {
    links: IPageLink[];
    activeLink?: IPageLink;
    onTabClick?: (link: IPageLink) => void;
    isLoading?: boolean;
    topTabSet?: boolean;
}

export const HorizontalNavContainerComponent = ({
    links,
    activeLink,
    onTabClick,
    isLoading,
    topTabSet
}: HorizontalNavContainerComponentProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [currentActiveLink, setCurrentActiveLink] = useState<IPageLink | undefined>(activeLink);
    const [currentActiveUrl, setCurrentActiveUrl] = useState<string | undefined>(activeLink?.url);

    const hasLinks = !!links?.length;
    const { normalizePath, findMatchingTopTab, findAssociatedTopTab } = useTopTabNav(links);

    const handleLinkClick = (link: IPageLink) => {
        onTabClick?.(link);

        setCurrentActiveLink(link);
        setCurrentActiveUrl(link?.url);

        if (!topTabSet) {
            return;
        }

        if (link?.url) {
            const nextPath = `/admin/${[link.url, link.defaultTab].filter(Boolean).join('/')}`;
            navigate(nextPath);
            return;
        }

        if (link?.command) {
            link.command();
        }
    };

    useEffect(() => {
        setCurrentActiveLink(activeLink);
        setCurrentActiveUrl(activeLink?.url);
    }, [activeLink]);

    useEffect(() => {
        if (!topTabSet || !hasLinks) {
            return;
        }

        const pathname = normalizePath(location.pathname);

        if (pathname === '' || pathname === '/') {
            setCurrentActiveLink(undefined);
            setCurrentActiveUrl(undefined);
            return;
        }

        const matchingLink = findMatchingTopTab(location.pathname);

        const associatedLink = findAssociatedTopTab(location.pathname);

        if (matchingLink) {
            setCurrentActiveLink(matchingLink);
            setCurrentActiveUrl(matchingLink.url);
            return;
        }

        if (associatedLink) {
            setCurrentActiveLink(associatedLink);
            setCurrentActiveUrl(associatedLink.url);
            return;
        }

        setCurrentActiveLink(undefined);
        setCurrentActiveUrl(undefined);
    }, [location.pathname, links, topTabSet, hasLinks]);

    return (
        <HorizontalNavView
            links={links}
            activeLink={currentActiveLink}
            activeUrl={currentActiveUrl}
            onTabClick={handleLinkClick}
            isLoading={isLoading}
            topTabSet={topTabSet}
        />
    );
};
